import { window } from "global";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useUGGAssetsContext } from "@ugg/shared/components/UGGAssetsProvider";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { useLanguage } from "@ugg/shared/hooks/use-language-hooks";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { useAramMayhemPerChampionAugmentsByRarity, useAramMayhemAugmentManifest } from "@ugg/shared/hooks/aram-mayhem";
import { asArray } from "@ugg/shared/utils/safe-coerce";

interface AramMayhemSEOProps {
  className?: string;
  patch: string;
  displayPatch: string;
}

// Grammar-aware blurb assembly — split out because the four count
// cases (0 / 1 / 2 / 3) each need distinct wording ("Augment" vs
// "Augments", "is" vs "are", "an S+ pick" vs "both S+ picks" vs
// "each are S+ picks") and inlining the ternaries would obscure
// what the actual sentence looks like at each length.
//
// The caller must slice `augmentNames` to at most 3 entries before
// invoking this — the 3-item branch destructures the first three
// and would produce a broken sentence with more than that.
function buildAugmentBlurb(championName: string, augmentNames: string[], displayPatch: string): string {
  const closer = `Follow this quick guide that ranks every LoL ${championName} Aram Augment to win more in ${displayPatch}.`;

  // 0 augments — the user-specified fallback for champions that
  // don't have S+ picks in the tier list (typically because the
  // per-champion sample size hasn't cleared MIN_SAMPLES yet).
  // Also the state during initial load before the augment fetches
  // resolve — better to render meaningful text on first paint
  // than to blank the whole SEO block waiting for augment data.
  if (augmentNames.length === 0) {
    return `The best ARAM Mayhem Augments for ${championName}. ${closer}`;
  }

  if (augmentNames.length === 1) {
    return `The best ARAM Mayhem Augment for ${championName} is ${augmentNames[0]}, an S+ pick when playing ARAM Mayhem. ${closer}`;
  }

  if (augmentNames.length === 2) {
    return `The best ARAM Mayhem Augments for ${championName} are ${augmentNames[0]} and ${augmentNames[1]}, both S+ picks when playing ARAM Mayhem. ${closer}`;
  }

  // 3 (or accidentally more — we destructure the first three and
  // ignore any extras; the caller should have sliced already).
  const [a, b, c] = augmentNames;
  return `The best ARAM Mayhem Augments for ${championName} are ${a}, ${b}, and ${c}, each are S+ picks when playing ARAM Mayhem. ${closer}`;
}

// Companion to buildAugmentBlurb — produces a shorter, more
// scannable string aimed at HTML <meta name="description"> (which
// Google truncates around 155-160 characters in search snippets)
// and <meta property="og:description"> (used for Facebook, Twitter,
// Slack, Discord link previews). Same 0/1/2/3 grammar branching as
// the blurb, but front-loads the champion name and augment list
// since search snippets truncate from the RIGHT — the most
// important information needs to appear first.
//
// Length envelope for the 3-augment branch:
//   Best ARAM Mayhem Augments for <champion>: <a>, <b>, <c>. S+ picks to win in <patch>.
// With average-length names (~15 chars) and a normal champion name
// this lands around 115 chars — well under Google's 160-char
// truncation threshold. Worst case (3 long augment names + long
// champion name) lands around 145, still safe.
function buildAugmentMetaDescription(championName: string, augmentNames: string[], displayPatch: string): string {
  if (augmentNames.length === 0) {
    return `Best ARAM Mayhem Augments for ${championName}. Learn S+ picks to win in ${displayPatch}.`;
  }

  if (augmentNames.length === 1) {
    return `Best ARAM Mayhem Augment for ${championName}: ${augmentNames[0]}. S+ pick to win in ${displayPatch}.`;
  }

  if (augmentNames.length === 2) {
    return `Best ARAM Mayhem Augments for ${championName}: ${augmentNames[0]} and ${augmentNames[1]}. S+ picks to win in ${displayPatch}.`;
  }

  const [a, b, c] = augmentNames;
  return `Best ARAM Mayhem Augments for ${championName}: ${a}, ${b}, ${c}. S+ picks to win in ${displayPatch}.`;
}

export default function AramMayhemSEO(props: AramMayhemSEOProps) {
  const { className, displayPatch, patch } = props;
  const { championId } = useChampionProfileContext();

  const { i18n } = useTranslation();
  const location = useLocation();

  const [lang] = useLanguage();

  const { staging } = useUGGAssetsContext();
  const envDir = staging ? "staging" : "prod";
  const { data: buildChampionNames } = useJSONFetcher(
    `https://static.bigbrain.gg/assets/lol/riot_patch_update/${envDir}/seo-champion-names.json`,
    { ssr: true },
  );

  // Per-champion augments by rarity + augment manifest — the same two URLs
  // AramMayhemAugments (the page body) hits, so useJSONFetcher's URL-keyed
  // cache dedupes both: zero additional network requests attributable to this
  // SEO block. `patch` and `championId` come from the same sources that module
  // uses (route props + ChampionProfileContext), so the cache keys line up
  // byte-for-byte. `ssr: true` on both so the blurb renders with real augment
  // names in the initial HTML for crawlers rather than hydrating in after mount.
  //
  // Sharing the body's feed is the point, not a convenience: the blurb names
  // the same three augments the reader sees first, from the same file, so the
  // two cannot disagree. (It previously read the per-champion TIER list, which
  // is a different file that merely happened to agree.)
  const { data: perChampionAugmentsByRarity } = useAramMayhemPerChampionAugmentsByRarity({
    championKey: championId,
    patch,
    ssr: true,
  });
  const { data: augmentManifest } = useAramMayhemAugmentManifest({
    patch,
    ssr: true,
  });

  if (!window) {
    if (location.pathname.includes("/tr_tr")) {
      i18n.changeLanguage("tr_TR");
    }
  }

  if (!buildChampionNames) {
    return null;
  }

  const champion = buildChampionNames[championId];
  if (!champion) {
    return null;
  }
  const { name } = champion;

  // Resolve the champion's top three PRISMATIC augment ids into display names
  // via the manifest. Prismatic is the strongest rarity and the first section
  // the page renders, so these are the three augments a reader sees before
  // anything else — taking the blurb from the same slice keeps the two in step.
  //
  // The array is already ordered best-first by the pipeline (no tiers in this
  // feed, see hooks/aram-mayhem.ts), so `slice(0, 3)` IS the top three and no
  // sort is needed here.
  //
  // Defensive access all the way down — any of these being missing (feed still
  // loading, rarity absent, manifest entry absent for an id) lands us at a
  // shorter or empty augmentNames list, which buildAugmentBlurb handles by
  // falling through to the matching count-branch (0 → no-augments fallback,
  // 1 → single form, 2 → paired form, 3 → full form). asArray covers a
  // wrong-typed rarity, since these types are an assertion about a remote file.
  //
  // Manifest keys are stringified augment ids (`str(aug_id)` on the Python side
  // in build_augment_manifest), so we String() the numeric id before the lookup.
  const topPrismaticAugmentIds = asArray(perChampionAugmentsByRarity?.rarities?.kPrismatic);
  const topAugmentNames = topPrismaticAugmentIds
    .slice(0, 3)
    .map((id) => augmentManifest?.[String(id)])
    .filter((n): n is string => Boolean(n));

  const blurb = buildAugmentBlurb(name, topAugmentNames, displayPatch);

  // Meta description — same augment data as the visible blurb, but
  // formatted as a shorter, search-snippet-shaped string. Emitted
  // via react-helmet-async which de-duplicates <meta> tags by their
  // `name` / `property` attribute — since AramMayhemSEO is deeper
  // in the component tree than whatever ancestor is currently
  // setting the default `description` / `og:description` for
  // champion pages, our tags win at SSR-render time. Verified via
  // curl on the deployed page (`grep -oE '<meta name="description"
  // content="[^"]*"'`).
  //
  // Both <meta name="description"> AND <meta property="og:description">
  // are overridden so search engines (which read the former) and
  // social/link-preview surfaces (which read the latter) stay in
  // sync — leaving one at the ancestor default while overriding
  // the other would produce inconsistent snippet content across
  // surfaces.
  const metaDescription = buildAugmentMetaDescription(name, topAugmentNames, displayPatch);

  return (
    <div className={classNames(className)}>
      <Helmet>
        <meta http-equiv="content-language" content={lang} />
        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
      </Helmet>
      {blurb}
    </div>
  );
}
